import axios, { AxiosRequestConfig } from 'axios';
import AuthUtil from '../helpers/AuthUtil';
import hash from "object-hash";
import processManager from '../helpers/ProcessManager';
import i18 from '@/i18nextConf';
import { CAMUNDA_URL } from '@/core/config';


const wenv = process.env;
const basePath = process.env.PUBLIC_URL;

interface Config {
    url: string | undefined;
    method: string | undefined;
    data: any;
}

let tabOwnerLogin: string | null = null;

const getLoginFromRaw = (raw: string | null): string | null => {
    try {
        return JSON.parse(raw || '{}')?.user?.login || null;
    } catch {
        return null;
    }
};

/**
 * Причины принудительного выхода складываем в localStorage (последние 10),
 * чтобы по жалобе "меня выкинуло на /login" не гадать по коду: у пользователя
 * в DevTools -> Application -> Local Storage -> authRedirectLog видно,
 * что именно сработало и когда.
 */
const AUTH_REDIRECT_LOG_KEY = 'authRedirectLog';

let redirectInProgress = false;

export const redirectToLogin = (reason: string, details?: Record<string, unknown>) => {
    // Пачка параллельных запросов падает пачкой же - причину пишем и уходим
    // один раз, иначе лог забивается десятком одинаковых записей.
    if (redirectInProgress) return;
    redirectInProgress = true;

    const entry = {
        reason,
        at: new Date().toISOString(),
        from: `${window.location.pathname}${window.location.search}`,
        ...details
    };

    try {
        console.warn('[auth] редирект на /login', entry);
        const prev = JSON.parse(localStorage.getItem(AUTH_REDIRECT_LOG_KEY) || '[]');
        const log = Array.isArray(prev) ? prev : [];
        log.push(entry);
        localStorage.setItem(AUTH_REDIRECT_LOG_KEY, JSON.stringify(log.slice(-10)));
    } catch {
        // логирование не должно мешать самому выходу
    }

    (window as any).location.href = `${basePath}/login`;
};

/**
 * Отдельный клиент для /auth/refresh: у него нет глобальных интерсепторов,
 * поэтому 401 от самого refresh не уходит в рекурсивное обновление токена.
 */
const refreshClient = axios.create();

let refreshPromise: Promise<any> | null = null;

const readUserContext = (): any => {
    try {
        return JSON.parse(localStorage.getItem('userContext') || '{}');
    } catch {
        return {};
    }
};

/**
 * Ответ refresh мержим в текущий контекст, а не заменяем целиком: бэк может
 * вернуть только токены, и полная замена стирала бы user (а вместе с ним
 * ломала контроль владельца вкладки ниже) и auth2Token.
 */
const mergeUserContext = (authData: any) => {
    const prev = readUserContext();
    const merged = {
        ...prev,
        ...(authData || {}),
        token: { ...(prev?.token || {}), ...(authData?.token || {}) },
        user: authData?.user ?? prev?.user
    };

    localStorage.setItem('userContext', JSON.stringify(merged));
    return merged;
};

/**
 * Single-flight: пока одно обновление токена в полёте, остальные 401
 * ждут его результат. Иначе пачка параллельных запросов (очереди, заявки,
 * тикеты) шлёт столько же refresh с одним refreshToken, и при одноразовых
 * refresh-токенах все, кроме первого, падают и выкидывают на /login.
 */
const refreshTokens = (refreshToken: string): Promise<any> => {
    if (!refreshPromise) {
        refreshPromise = refreshClient
            .post('/sonata/api/auth/refresh', { refreshToken }, { baseURL: CAMUNDA_URL })
            .then((res) => mergeUserContext(res.data))
            .finally(() => {
                refreshPromise = null;
            });
    }

    return refreshPromise;
};

const plainHeaders = (headers: any): Record<string, any> => {
    if (!headers) return {};
    return typeof headers.toJSON === 'function' ? { ...headers.toJSON() } : { ...headers };
};

const withCurrentToken = (config: AxiosRequestConfig): AxiosRequestConfig => {
    const ctx = readUserContext();
    const headers = plainHeaders(config.headers);

    // cache-заголовок на повторе не нужен: он бы отдал ответ из локального кеша
    delete headers.cache;
    headers.Authorization = 'Bearer ' + (ctx?.token || {}).accessToken;
    headers.Auth2 = 'Code ' + (ctx?.token || {}).auth2Token;

    config.headers = headers;
    (config as any)._retry = true;
    return config;
};

export class Server {
    constructor() {
        window.addEventListener('storage', (e) => {
            if (e.key !== 'userContext') return;

            const oldLogin = getLoginFromRaw(e.oldValue);
            const newLogin = getLoginFromRaw(e.newValue);

            if (newLogin !== oldLogin) {
                redirectToLogin('storage-event: userContext сменился в другой вкладке', {
                    oldLogin,
                    newLogin
                });
            }
        });


        axios.interceptors.request.use(
            function (config) {
                if (config.headers?.cache) {
                    const _config: Config = {
                        url: config.url,
                        method: config.method,
                        data: JSON.stringify(config.data)
                    };
                    const requestHash = hash(_config);
                    const items = processManager.getCacheItems();
                    if (processManager.hasCacheKey(requestHash)) {
                        throw { isLocal: true, response: { data: items[requestHash] } };
                    }
                }

                return config;
            },
            function (error) {
                return error?.isLocal
                    ? Promise.resolve(error)
                    : Promise.reject(error);
            }
        );

        axios.interceptors.response.use(
            function (response) {
                const { config } = response;
                if (config.headers?.cache) {
                    var _config: Config = {
                        url: config.url,
                        method: config.method,
                        data: JSON.stringify(config.data)
                    };
                    var requestHash = hash(_config);
                    processManager.setCacheItem(requestHash, response.data);
                }
                return response;
            },
            async function (error) {
                if (!error.response) {
                    processManager.stopLoading();
                } else if (error.response.status === 401) {
                    const originalConfig = error.config as AxiosRequestConfig | undefined;
                    const auth = AuthUtil.getAuth();
                    const refreshToken = auth?.token?.refreshToken;

                    if (!originalConfig) {
                        return Promise.reject(error);
                    }

                    // Повтор уже был - второй 401 означает, что дело не в протухшем
                    // токене. Ещё один refresh только зациклит запрос.
                    if ((originalConfig as any)._retry) {
                        localStorage.setItem('redirectUrlAfterLogin', window.location.pathname);
                        redirectToLogin('401 после повтора с обновлённым токеном', {
                            url: originalConfig.url
                        });
                        return Promise.reject(error);
                    }

                    if (!refreshToken) {
                        localStorage.setItem('redirectUrlAfterLogin', window.location.pathname);
                        redirectToLogin('401 без refreshToken в localStorage', {
                            url: originalConfig.url
                        });
                        return Promise.reject(error);
                    }

                    // Запрос ушёл со старым accessToken, который к этому моменту уже
                    // обновил кто-то другой - обновлять повторно нечего, просто
                    // повторяем с актуальным токеном.
                    const sentToken = plainHeaders(originalConfig.headers).Authorization;
                    const currentToken = 'Bearer ' + (auth?.token || {}).accessToken;
                    if (sentToken && sentToken !== currentToken) {
                        return await axios.request(withCurrentToken(originalConfig));
                    }

                    try {
                        await refreshTokens(refreshToken);
                    } catch (e: any) {
                        localStorage.setItem('redirectUrlAfterLogin', window.location.pathname);
                        redirectToLogin('не удалось обновить токен по refreshToken', {
                            url: originalConfig.url,
                            refreshStatus: e?.response?.status
                        });
                        return Promise.reject(error);
                    }

                    return await axios.request(withCurrentToken(originalConfig));
                } else if (error?.isLocal) {
                    console.log("Came here", error);
                    return Promise.resolve(error.response);
                }
                else {
                    // silentError в конфиге запроса: вызывающий сам решает, показывать ли ошибку.
                    // Нужен там, где отказ бэка — ожидаемый исход, а не сбой
                    if (error.config?.silentError) {
                        // снекбар не показываем
                    } else if (error.response && error.response.data && error.response.data.message) {
                        processManager.openSnackBar(i18.t(error.response.data.message), { severity: "error" });
                    } else if (error.response && error.response.data && error.response.data.Message) {
                        processManager.openSnackBar(i18.t(error.response.data.Message), { severity: "error" });
                    }

                    processManager.stopLoading();
                    processManager.closeBackDrop();
                }
                return Promise.reject(error);
            }
        );
    }

    public request(config: any) {
        return axios.request(config);
    }
    public get(url: string, config = {} as any): any {
        config = this.getConfig(config);
        return axios.get(url, config).then((r) => r.data);
    }
    public getFormData(url: string, data: any, config = {} as any): any {
        config = this.getConfig(config);
        const api = axios.create(config);
        return api.get(url, data).then((r) => r.data);
    }
    public getNoAuth(url: string, config = {} as any): any {
        config = config || {};
        config.headers = config.headers || {};
        config.baseURL = config.baseURL || wenv.SERVER_URL;
        return axios.get(url, config).then((r) => r.data);
    }
    public delete(url: string, config = {} as any): any {
        config = this.getConfig(config);
        return axios.delete(url, config).then((r) => r.data);
    }
    public head(url: string, config = {} as any): any {
        config = this.getConfig(config);
        return axios.head(url, config);
    }
    public token(url: string, data: any, config = {} as any) {
        config.baseURL = config.baseURL || wenv.SERVER_URL;
        return axios.post(url, data, config).then((r) => r.data);
    }
    public post(url: string, data: any, config = {} as any): any {
        config = this.getConfig(config);
        return axios.post(url, data, config).then((r) => r.data);
    }

    public postPublic(url: string, data: any, config = {} as any): any {
        config = config || {};
        config.headers = config.headers || {};
        config.baseURL = config.baseURL || wenv.SERVER_URL;
        config.headers.Authorization = 'Bearer eyJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJwdWJsaWMiLCJmdWxsTmFtZSI6IlB1YmxpYyBQdWJsaWMiLCJlbWFpbCI6InB1YmxpY0BxdWlxLmt6IiwibGFuZyI6IktBWiIsImdtdCI6IkFzaWEvQWxtYXR5IiwidGl0bGUiOiJRVUlRIiwiYXV0aCI6W3siY29kZSI6IlBVQkxJQyIsImRlc2NyaXB0aW9uIjoicHVibGljIiwiY29tbWVudHMiOiJwdWJsaWMiLCJhdXRob3JpdHkiOiJQVUJMSUMifV0sImFpbSI6ImFjY2VzcyIsInR5cGUiOiJTWVNURU0iLCJpYXQiOjE2ODMxMDA1NzUsImV4cCI6MTcxNDYzNjU3NX0.NxvIMBGSdgwiAAbgK-bRuxhtwqek5VeNAj5PuEzV8U4';
        return axios.post(url, data, config).then((r) => r.data);
    }

    public postNoAuth(url: string, data: any, config = {} as any): any {
        config = config || {};
        config.headers = config.headers || {};
        config.baseURL = config.baseURL || wenv.SERVER_URL;
        return axios.post(url, data, config).then((r) => r.data);
    }
    public put(url: string, data: any, config = {} as any): any {
        config = this.getConfig(config);
        return axios.put(url, data, config).then((r) => r.data);
    }
    public patch(url: string, data: any, config = {} as any): any {
        config = this.getConfig(config);
        return axios.patch(url, data, config).then((r) => r.data);
    }

    public getConfig(config = {} as any) {
        config = config || {};
        config.headers = config.headers || {};
        var userContext = JSON.parse(localStorage.getItem('userContext') || '{}');
        var currentLogin = userContext?.user?.login || null;

        if (tabOwnerLogin === null) {
            tabOwnerLogin = currentLogin;
        } else if (currentLogin !== tabOwnerLogin) {
            redirectToLogin('getConfig: логин в localStorage не совпадает с владельцем вкладки', {
                tabOwnerLogin,
                currentLogin
            });
            throw new Error('Session owner changed in another tab');
        }

        config.headers.Authorization = 'Bearer ' + (userContext.token || {}).accessToken;
        config.headers.Auth2 = 'Code ' + (userContext.token || {}).auth2Token;
        config.baseURL = config.baseURL || wenv.SERVER_URL;
        return config;
    }
}
export const server = new Server();
